import { Chip, Skeleton, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { getModelTypeParameters } from 'src/api/queries';
import { DetailSection, DetailRow } from '../details';
import GenericError from 'src/components/GenericError';
import PermissionGate from 'src/permission-management/PermissionGate';
import { formatDateTimeShort } from 'src/utils/dateUtil';

const ParametersSection = ({ modelType }) => {
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: [getModelTypeParameters(modelType.id).key, modelType.id],
    queryFn: () => getModelTypeParameters(modelType.id).func('page_size=300')
  });

  const count = query.data?.results?.length ?? 0;
  const description = query.isLoading
    ? 'Loading…'
    : `Assigned to ${count} parameter${count === 1 ? '' : 's'}.`;

  let body;
  if (query.isLoading) {
    body = (
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" width={120} height={32} />
        ))}
      </Stack>
    );
  } else if (query.isError) {
    body = <GenericError error={query.error} />;
  } else {
    const items = query.data?.results ?? [];
    body = items.length ? (
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
        {items.map((mp) => (
          <Chip
            key={`parameter-chip-${mp.parameter.id}`}
            label={mp.parameter.name}
            variant="outlined"
            clickable
            onClick={() => navigate(`/dashboard/parameters/${mp.parameter.id}`)}
          />
        ))}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.disabled">
        No parameters assigned yet.
      </Typography>
    );
  }

  // The chip row isn't a label/value pair, so render it inline without
  // DetailRow — DetailSection's body Stack with dividers would split
  // the chips otherwise.
  return (
    <DetailSection title="Parameters on This Model" description={description}>
      <Stack sx={{ pt: 1 }}>{body}</Stack>
    </DetailSection>
  );
};

const ModelTypeDetailTable = ({ modelType }) => (
  <Stack spacing={3}>
    <DetailSection title="General">
      <DetailRow label="Name">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {modelType.name}
        </Typography>
      </DetailRow>
      <DetailRow label="Visual name">{modelType.visual_name}</DetailRow>
      <DetailRow label="Prefix">
        {modelType.prefix ? (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {modelType.prefix}
          </Typography>
        ) : null}
      </DetailRow>
      <DetailRow label="Receipt header size">
        {modelType.receipt_header_size != null ? String(modelType.receipt_header_size) : ''}
      </DetailRow>
      <DetailRow label="Created">{formatDateTimeShort(modelType.created_at)}</DetailRow>
      <DetailRow label="Updated">{formatDateTimeShort(modelType.updated_at)}</DetailRow>
    </DetailSection>

    <PermissionGate permissions={['commonlib.view_parameter', 'commonlib.view_modeltypeparameter']}>
      <ParametersSection modelType={modelType} />
    </PermissionGate>
  </Stack>
);

ModelTypeDetailTable.propTypes = {
  modelType: PropTypes.object.isRequired,
};

export default ModelTypeDetailTable;
